/* eslint-disable max-lines-per-function */
import React, { PropsWithChildren } from "react";
import * as yup from "yup";
import { Switch, useMediaQuery, Grid } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import {
  MaterialFormikSelect as Select,
  MaterialFormikSelectAutocomplete as SelectAutocomplete,
} from "components/haven-design/form/select";
import { CheckboxWithErrorLabel as Checkbox } from "components/haven-design/form/checkbox";
import { MultiSelect } from "components/haven-design/form/select/multi";
import { unitSizeMetadata } from "model/interest/unit-size";
import theme from "components/theme";
import { ListBuildingUnits_buildingUnits as BuildingUnitType } from "types/gql-types";
import { AccessibilityOption, FundingSourceOption, PreferenceOption } from "./unit-add-form";
import { ToggleSwitches } from "./unit-batch-edit-form";

const accessibilityOptions = ["mobility", "visual", "hearing"] as AccessibilityOption[];
const fundingSourceOptions = [
  "Tax Credit",
  "Tax Exempt Bond",
  "HPD ELLA",
  "HUD Rental Assistance",
  "HUD Project-Based Voucher",
  "HUD Tenant Based Housing Choice Voucher",
] as FundingSourceOption[];
const preferenceOptions = [
  "Age Restricted (55+)", "Age Restricted (62+)", "Aging out of Foster Care",
  "Artist", "Disabled", "Displaced", "Domestic Violence Survivor", "Elderly/Disabled (62+)",
  "Employer-Specific Employee", "Experiencing Homelessness", "Live Preference", "Live/Work Preference",
  "Teacher", "Veteran", "Work Preference",
] as PreferenceOption[];

export const sharedFieldsValidations = {
  bedrooms: yup.string(),
  occupancyMin: yup.number().min(1).max(15),
  occupancyMax: yup.number().min(1).max(15),
  sqft: yup.number().min(0),
  floor: yup.number().min(0),
  occupied: yup.string(),
  accessibility: yup.array(yup.string().oneOf(accessibilityOptions)),
  fundingSource: yup.array(yup.string().oneOf(fundingSourceOptions)),
  preference: yup.array(yup.string().oneOf(preferenceOptions)),
  bathrooms: yup.number()
    .test(
      "is-decimal",
      "The bathroom number must be divisible by 0.25",
      (inputValue) => !inputValue || (inputValue * 4) % 1 === 0,
    )
    .min(0, "The bathroom number must be non-negative"),
  rentType: yup.string().oneOf(["fixed", "relative"]).required(),
  incomeDesignation: yup.string().oneOf(["none", "30", "60", "80"]).required(),
  rentValue: yup.number().when("rentType", {
    is: "fixed", // if rentType is 'fixed'
    then: (schema) => schema.min(0).required(), // rentValue must be non-negative
    otherwise: (schema) =>
      schema.min(0).max(100, "Must be less than or equal to 100").required(), // else, rentValue must be between 0 and 100
  }),
};

type ToggleProps = {name: string, toggle: boolean}

export type BuildingUnitSharedType = Omit<BuildingUnitType, "id" | "name" | "__typename" | "buildingId">

const FormikSwitch: React.FC<{name: string}> = ({ name }) => {
  const [, { value }, { setValue }] = useField(name);

  const isSmall = useMediaQuery(theme.breakpoints.down("xs"));

  return <div style={isSmall ? { marginLeft: "-14px" } : {}}>
    <Switch name={name}
      checked={value}
      onChange={() => setValue(!value)}
    />
  </div>;
};

const WithToggle: React.FC<PropsWithChildren<ToggleProps>> =
  ({ toggle, children, name }) => {
    if (!toggle) return <>{children}</>;
    return <Grid container size={{ xs: 12 }} spacing={2}>
      <Grid size={{ xs: 2, md: 1 }}>
        <FormikSwitch name={name} />
      </Grid>
      <Grid size={{ xs: 10, md: 11 }} container spacing={2}>
        {children}
      </Grid>
    </Grid>;
  };


const bedroomOptions = unitSizeMetadata
  .map(({ id, label }) => ({ label, value: id }));

const UnitFormSharedFields: React.FC<{toggleUpdate?: boolean}> =
({ toggleUpdate: toggle = false }) => {
  const required = !toggle;
  const { values } = useFormikContext<BuildingUnitType & {toggle: ToggleSwitches}>();
  const rentValueLimit = {} as {max?: string};
  if (values.rentType === "relative") rentValueLimit.max = "100";
  return <>
    <WithToggle name="toggle.bedrooms" toggle={toggle}>
      <Grid size={{ xs: 12, md: 6 }}>
        <SelectAutocomplete margin="none"
          required={required}
          name="bedrooms"
          label="Bedrooms"
          options={bedroomOptions}
          disabled={toggle && !values.toggle.bedrooms}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.bathrooms" toggle={toggle}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Input
          margin="none"
          name="bathrooms"
          type="number"
          disabled={toggle && !values.toggle.bathrooms}
          slotProps={{ htmlInput: { min: "0", step: "0.25" } }}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.rent" toggle={toggle}>
      <Grid size={{ xs: 8 }}>
        <Select
          title="Rent Type"
          margin="none"
          name="rentType"
          disabled={toggle && !values.toggle.rent}
          options={[
            { label: "Fixed Value", value: "fixed" },
            { label: "Income Percentage", value: "relative" },
          ]}
        />
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Input
          label={values.rentType === "fixed" ? "Rent" : "Percentage"}
          margin="none"
          name="rentValue"
          type="number"
          slotProps={{ htmlInput: { min: "0", ...rentValueLimit } }}
          disabled={toggle && !values.toggle.rent}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.occupancy" toggle={toggle}>
      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
        <Input
          label="Occupancy Min"
          margin="none"
          name="occupancyMin"
          type="number"
          disabled={toggle && !values.toggle.occupancy}
          slotProps={{ htmlInput: { min: "1", max: "15" } }}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
        <Input
          label="Occupancy Max"
          margin="none"
          name="occupancyMax"
          type="number"
          disabled={toggle && !values.toggle.occupancy}
          slotProps={{ htmlInput: { min: "1", max: "15" } }}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.sqft" toggle={toggle}>
      <Grid size={{ xs: 6, md: 3 }}>
        <Input
          margin="none"
          name="sqft"
          type="number"
          disabled={toggle && !values.toggle.sqft}
          slotProps={{ htmlInput: { min: "0" } }}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.floor" toggle={toggle}>
      <Grid size={{ xs: 6, md: 3 }}>
        <Input
          margin="none"
          name="floor"
          type="number"
          disabled={toggle && !values.toggle.floor}
          slotProps={{ htmlInput: { min: "0" } }}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.occupied" toggle={toggle}>
      <Grid size={{ xs: 6 }}>
        <Select
          title="Unit Status"
          margin="none"
          name="occupied"
          disabled={toggle && !values.toggle.rent}
          options={[
            { label: "Vacant", value: "Vacant" },
            { label: "Occupied", value: "Occupied" },
            { label: "Employee Unit", value: "Employee Unit" },
            { label: "Leased", value: "Leased" },
            { label: "Offline (turnover)", value: "Offline (turnover)" },
            { label: "Offline (construction)", value: "Offline (construction)" },
            { label: "Offline (rehab)", value: "Offline (rehab)" },
          ]}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.accessibility" toggle={toggle}>
      <Grid size={{ xs: 12, md: 6 }}>
        <MultiSelect
          fullWidth
          margin="none"
          name="accessibility"
          label="Accessibility"
          options={accessibilityOptions}
          disabled={toggle && !values.toggle.accessibility}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.fundingSource" toggle={toggle}>
      <Grid size={{ xs: 12, md: 6 }}>
        <MultiSelect
          fullWidth
          margin="none"
          name="fundingSource"
          label="Funding Sources"
          options={fundingSourceOptions}
          disabled={toggle && !values.toggle.fundingSource}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.incomeDesignation" toggle={toggle}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Select
          title="Income Designation"
          margin="none"
          name="incomeDesignation"
          disabled={toggle && !values.toggle.incomeDesignation}
          options={[
            { label: "None", value: "none" },
            { label: "30%", value: "30" },
            { label: "60%", value: "60" },
            { label: "80%", value: "80" },
          ]}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.preference" toggle={toggle}>
      <Grid size={{ xs: 12 }}>
        <MultiSelect
          fullWidth
          margin="none"
          name="preference"
          label="Preference"
          options={preferenceOptions}
          disabled={toggle && !values.toggle.preference}
        />
      </Grid>
    </WithToggle>
    <WithToggle name="toggle.referral" toggle={toggle}>
      <Grid size={{ xs: 12 }}>
        <Checkbox
          name="isReferral"
          label="Referral Unit"
          disabled={toggle && !values.toggle.referral}
        />
        {values.isReferral && <Input
          margin="none"
          label="Referral Organization"
          name="referralOrg"
          type="text"
          slotProps={{ htmlInput: { min: "0" } }}
          disabled={toggle && !values.toggle.referral}
        />}
      </Grid>
    </WithToggle>
  </>;
};

export default UnitFormSharedFields;
